import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  Spinner,
  Table,
  TableWrap,
  Tabs,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Category, type CategoryField, type Location } from '../api';

/**
 * The two lists everything else points at.
 *
 * This page had to exist before anything could be put in the register at all:
 * an asset needs a category, the API could create one, and nothing on screen
 * could. The first run of this app ended at an empty list with no way forward.
 *
 * Categories carry the custom-field schema, which is the answer to "store
 * every detail of every accessory" without sixty columns that are blank on
 * every row. A laptop category asks for RAM and warranty; a USB hub asks for
 * port count; neither form shows the other's questions.
 */
const FIELD_TYPES = [
  { id: 'text', label: 'Text' },
  { id: 'number', label: 'Number' },
  { id: 'date', label: 'Date' },
  { id: 'select', label: 'One of a list' },
] as const;

const LOCATION_KINDS = [
  { id: 'office', label: 'Office' },
  { id: 'store', label: 'Storeroom' },
  { id: 'home', label: 'Someone’s home' },
  { id: 'vendor', label: 'With a vendor' },
  { id: 'other', label: 'Somewhere else' },
] as const;

export function Catalog() {
  const [tab, setTab] = useState('categories');

  return (
    <>
      <Toolbar>
        <Tabs
          items={[
            { id: 'categories', label: 'Categories' },
            { id: 'locations', label: 'Places' },
          ]}
          value={tab}
          onChange={setTab}
          label="Catalog"
        />
      </Toolbar>

      {tab === 'categories' ? <Categories /> : <Locations />}
    </>
  );
}

/* ------------------------------------------------------------ categories -- */

function Categories() {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [removing, setRemoving] = useState<Category | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const result = await api.get<{ categories: Category[] }>('/api/categories');
    setCategories(result.categories);
  }, []);

  useEffect(() => {
    load().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load categories.'),
    );
  }, [load]);

  async function remove() {
    if (!removing) return;
    try {
      await api.del(`/api/categories/${removing.id}`);
      toast.show(`${removing.name} is gone.`);
      setRemoving(null);
      await load();
    } catch (caught) {
      // The server refuses with the count in it -- "3 things are in this
      // category" -- which is more use than anything worded here.
      toast.show(caught instanceof Error ? caught.message : 'Could not delete it.', {
        tone: 'danger',
      });
    }
  }

  if (error) {
    return (
      <Notice tone="danger" title="Could not load categories">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <div className="row-actions" style={{ marginBottom: 12 }}>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Add a category
        </Button>
      </div>

      {!categories ? (
        <Spinner />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="A category is the kind of thing something is — Laptops, Monitors, Cables. Nothing can be added to the register until there is one."
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Counted how</th>
                  <th>Extra details asked for</th>
                  <th className="num">In it</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td className="tcell-name">{category.name}</td>
                    <td>
                      <Badge tone={category.tracking === 'tracked' ? 'brand' : 'accent'}>
                        {category.tracking === 'tracked' ? 'one by one' : 'in bulk'}
                      </Badge>
                    </td>
                    <td className="sub">
                      {category.fieldSchema.length === 0
                        ? '—'
                        : category.fieldSchema.map((field) => field.label).join(', ')}
                    </td>
                    <td className="num">{category.assetCount + category.stockCount}</td>
                    <td>
                      <div className="row-actions">
                        <Button variant="ghost" onClick={() => setEditing(category)}>
                          Edit
                        </Button>
                        <Button variant="ghost" onClick={() => setRemoving(category)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {editing && (
        <EditCategory
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          open
          danger
          title={`Delete ${removing.name}?`}
          confirmLabel="Delete it"
          onConfirm={() => void remove()}
          onClose={() => setRemoving(null)}
        >
          <p>
            Only possible while nothing is in it. Anything already recorded in this category keeps
            it.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

function EditCategory({
  category,
  onClose,
  onSaved,
}: {
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(category?.name ?? '');
  const [tracking, setTracking] = useState(category?.tracking ?? 'tracked');
  const [fields, setFields] = useState<CategoryField[]>(category?.fieldSchema ?? []);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  function addField() {
    setFields((was) => [...was, { key: '', label: '', type: 'text' }]);
  }

  function setField(index: number, patch: Partial<CategoryField>) {
    setFields((was) => was.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  }

  /*
   * The key is derived from the label rather than typed.
   *
   * It is what the value is stored under, so changing it later orphans every
   * value already recorded against it. Deriving it means nobody has to know
   * that, and an existing field keeps the key it was saved with even if the
   * label is reworded.
   */
  function labelChanged(index: number, label: string) {
    const existing = category?.fieldSchema[index];
    const key = existing?.key ?? label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    setField(index, { label, key });
  }

  async function save() {
    setBusy(true);
    try {
      const fieldSchema = fields
        .filter((field) => field.label.trim() && field.key)
        .map((field) => ({
          ...field,
          options:
            field.type === 'select'
              ? (field.options ?? []).filter((option) => option.trim())
              : undefined,
        }));

      if (category) {
        await api.patch(`/api/categories/${category.id}`, { name, fieldSchema });
      } else {
        await api.post('/api/categories', { name, tracking, fieldSchema });
      }
      toast.show(category ? 'Saved.' : `${name} added.`);
      onSaved();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not save it.', {
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="md"
      title={category ? `Edit ${category.name}` : 'Add a category'}
      onClose={onClose}
    >
      <Field label="Name" required hint="What somebody would call this kind of thing.">
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Laptops" />
      </Field>

      {category ? (
        // Changing this would orphan everything already in the category, so
        // the server does not accept it either. Shown rather than hidden, so
        // the answer to "why can I not change this" is on screen.
        <Field label="Counted how" hint="Fixed once a category exists. Make a new one instead.">
          <Input value={tracking === 'tracked' ? 'One by one' : 'In bulk'} disabled />
        </Field>
      ) : (
        <Field
          label="Counted how"
          required
          hint="Laptops get a row each. Cables are counted — nobody wants to tag fifty of them."
        >
          <Select value={tracking} onChange={(event) => setTracking(event.target.value as 'tracked' | 'stock')}>
            <option value="tracked">One by one, each with its own record</option>
            <option value="stock">In bulk, counted rather than numbered</option>
          </Select>
        </Field>
      )}

      <div style={{ marginTop: 18 }}>
        <div className="about-panel__head">
          <strong>Extra details to ask for</strong>
          <Button variant="ghost" onClick={addField}>
            Add a detail
          </Button>
        </div>
        <p className="sub" style={{ marginTop: 2 }}>
          Only for this category. A laptop asks for RAM; a hub asks for how many ports.
        </p>

        {fields.length === 0 ? (
          <p className="muted" style={{ marginTop: 8 }}>
            None. The standard details — make, model, serial, tag — are always asked for.
          </p>
        ) : (
          fields.map((field, index) => (
            <div key={index} className="catalog-field">
              <Input
                value={field.label}
                onChange={(event) => labelChanged(index, event.target.value)}
                placeholder="Memory"
              />
              <Select
                value={field.type}
                onChange={(event) =>
                  setField(index, { type: event.target.value as CategoryField['type'] })
                }
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </Select>
              {field.type === 'select' ? (
                <Input
                  value={(field.options ?? []).join(', ')}
                  onChange={(event) =>
                    setField(index, { options: event.target.value.split(',').map((o) => o.trim()) })
                  }
                  placeholder="8 GB, 16 GB, 32 GB"
                />
              ) : (
                <span />
              )}
              <Button variant="ghost" onClick={() => setFields((was) => was.filter((_, i) => i !== index))}>
                Remove
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="row-actions" style={{ marginTop: 18, justifyContent: 'flex-start' }}>
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
          {busy ? 'Saving…' : category ? 'Save' : 'Add it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------- locations -- */

function Locations() {
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Location | 'new' | null>(null);
  const [removing, setRemoving] = useState<Location | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const result = await api.get<{ locations: Location[] }>('/api/locations');
    setLocations(result.locations);
  }, []);

  useEffect(() => {
    load().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load places.'),
    );
  }, [load]);

  async function remove() {
    if (!removing) return;
    try {
      await api.del(`/api/locations/${removing.id}`);
      toast.show(`${removing.name} is gone.`);
      setRemoving(null);
      await load();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not delete it.', {
        tone: 'danger',
      });
    }
  }

  if (error) {
    return (
      <Notice tone="danger" title="Could not load places">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <div className="row-actions" style={{ marginBottom: 12 }}>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Add a place
        </Button>
      </div>

      {!locations ? (
        <Spinner />
      ) : locations.length === 0 ? (
        <EmptyState
          title="No places yet"
          description="Where things live when nobody has them — the store cupboard, the Bhubaneswar office, a vendor’s workshop."
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Place</th>
                  <th>Kind</th>
                  <th>Notes</th>
                  <th className="num">Things there</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td className="tcell-name">{location.name}</td>
                    <td className="sub">
                      {LOCATION_KINDS.find((k) => k.id === location.kind)?.label ?? location.kind}
                    </td>
                    <td className="sub">{location.notes || '—'}</td>
                    <td className="num">{location.assetCount}</td>
                    <td>
                      <div className="row-actions">
                        <Button variant="ghost" onClick={() => setEditing(location)}>
                          Edit
                        </Button>
                        <Button variant="ghost" onClick={() => setRemoving(location)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {editing && (
        <EditLocation
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          open
          danger
          title={`Delete ${removing.name}?`}
          confirmLabel="Delete it"
          onConfirm={() => void remove()}
          onClose={() => setRemoving(null)}
        >
          <p>
            {removing.assetCount > 0
              ? `${removing.assetCount} ${removing.assetCount === 1 ? 'thing says it is' : 'things say they are'} here. They will stop saying where they are, which is true — nothing else about them changes.`
              : 'Nothing is here.'}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

function EditLocation({
  location,
  onClose,
  onSaved,
}: {
  location: Location | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(location?.name ?? '');
  const [kind, setKind] = useState(location?.kind ?? 'office');
  const [notes, setNotes] = useState(location?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      if (location) {
        await api.patch(`/api/locations/${location.id}`, { name, kind, notes });
      } else {
        await api.post('/api/locations', { name, kind, notes });
      }
      toast.show(location ? 'Saved.' : `${name} added.`);
      onSaved();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not save it.', {
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title={location ? `Edit ${location.name}` : 'Add a place'} onClose={onClose}>
      <Field label="Name" required>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Store cupboard"
        />
      </Field>
      <Field label="Kind">
        <Select value={kind} onChange={(event) => setKind(event.target.value)}>
          {LOCATION_KINDS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Notes">
        <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>

      <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
          {busy ? 'Saving…' : location ? 'Save' : 'Add it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
